import { useMemo } from 'react';
import { useYandexKit } from '#shared/components/YandexKitProvider';
import { CONSTRUCTOR_MYSTIQUE_ID } from '../model/constants';
import { getConstructorSectionCount, useConstructorPageSettings, useConstructorSectionSettings } from '../model/slices';
import { useDenormalizedConstructorSection } from './useDenormalizedConstructorSection';

export const useAllowedToDeleteSection = (props: { pageId: string; sectionInstanceId: string }) => {
    const { pageId, sectionInstanceId } = props;

    const section = useConstructorSectionSettings(pageId, sectionInstanceId);
    const page = useConstructorPageSettings(pageId);
    const yandexKit = useYandexKit();
    const denormalizeSection = useDenormalizedConstructorSection();

    const { sectionSettings, widgetToCount } = useMemo(() => {
        if (section.widget !== CONSTRUCTOR_MYSTIQUE_ID) {
            return {
                sectionSettings: yandexKit.getSettings(section.widget),
                widgetToCount: section.widget,
            };
        }

        const denormalizedSection = denormalizeSection(section);

        if (!denormalizedSection) {
            return {
                sectionSettings: null,
                widgetToCount: section.widget,
            };
        }

        if ('template' in denormalizedSection && denormalizedSection.template.alias) {
            return {
                sectionSettings: yandexKit.getSettings(denormalizedSection.template.alias),
                widgetToCount: denormalizedSection.template.alias,
            };
        }

        return {
            sectionSettings: yandexKit.getSettings(denormalizedSection),
            widgetToCount: section.widget,
        };
    }, [yandexKit, section, denormalizeSection]);

    const allowedToDelete = useMemo(() => {
        const { totalCount, count } = getConstructorSectionCount(widgetToCount);
        const allowedToAdd =
            sectionSettings?.supportedPages && Array.isArray(sectionSettings.supportedPages)
                ? sectionSettings.supportedPages.some((supportedAlias) => supportedAlias === page?.alias)
                : true;
        const minimumPerPageCheck = sectionSettings?.minimum ? count > sectionSettings.minimum : true;
        const minimumPerWebsiteCheck =
            sectionSettings?.isUnique ||
            (sectionSettings?.totalMinimum ? totalCount > sectionSettings.totalMinimum : true);

        return allowedToAdd && minimumPerPageCheck && minimumPerWebsiteCheck;
    }, [sectionSettings, page, widgetToCount]);

    return allowedToDelete;
};
